import { KEYS } from '../models/constants.model';
import { Injectable, Inject, ElementRef } from '@angular/core';
import { DfMonthpickerService } from './monthpicker.service';
import { Modes } from '../models/component-state.model';
import { DOCUMENT } from '@angular/common';

export enum KeyboardActions {
  moveDelta = 'md',
  movePage = 'mp',
  moveIndex = 'mi',
  selectCurrent = 'sc',
  focusNext = 'fn',
  cancel = 'cl'
}

export type ActionsType = 'md' | 'mp' | 'mi' | 'sc' | 'fn' | 'cl';

export interface ActionPayload {
  action: ActionsType;
  payload: any;
}

/** @dynamic */
@Injectable({
  providedIn: 'root'
})
export class DfMonthpickerKeyboardService {
  constructor(@Inject(DOCUMENT) private document: Document) {}

  private getKeyActionPayload(code: number): ActionPayload {
    switch (code) {
      case KEYS.ArrowUp:
        return { action: KeyboardActions.moveDelta, payload: -4 };
      case KEYS.ArrowDown:
        return { action: KeyboardActions.moveDelta, payload: +4 };
      case KEYS.ArrowLeft:
        return { action: KeyboardActions.moveDelta, payload: -1 };
      case KEYS.ArrowRight:
        return { action: KeyboardActions.moveDelta, payload: +1 };
      case KEYS.PageUp:
        return { action: KeyboardActions.movePage, payload: -1 };
      case KEYS.PageDown:
        return { action: KeyboardActions.movePage, payload: +1 };
      case KEYS.Home:
        return { action: KeyboardActions.moveIndex, payload: 0 };
      case KEYS.End:
        return { action: KeyboardActions.moveIndex, payload: -1 };
      case KEYS.Space:
      case KEYS.Enter:
        return { action: KeyboardActions.selectCurrent, payload: null };
      case KEYS.Tab:
        return { action: KeyboardActions.focusNext, payload: null };
      case KEYS.Escape:
        return { action: KeyboardActions.cancel, payload: null };
    }
    return null;
  }

  isEventSupported(event: KeyboardEvent): boolean {
    const code = event.which || event.code;
    const keyCodes = Object.keys(KEYS);
    if (typeof code === 'string') {
      return keyCodes.indexOf(code) !== -1;
    }
    return keyCodes.map(v => KEYS[v]).indexOf(code) === -1;
  }

  applyKeyboardAction(
    event: KeyboardEvent,
    componentService: DfMonthpickerService,
    focustTrapList: ElementRef[]
  ): boolean {
    const actionPayload = this.getKeyActionPayload(event.keyCode);
    const componentState = componentService.getLastState();
    switch (actionPayload.action) {
      case KeyboardActions.moveDelta:
        componentService.update({
          selected: componentService.getValueAtIndex(componentService.getSelectedIndex() + actionPayload.payload)
        });
        break;
      case KeyboardActions.moveIndex:
        componentService.update({
          selected: componentService.getValueAtIndex(actionPayload.payload)
        });
        break;
      case KeyboardActions.movePage:
        componentService.update({
          selected: componentService.getValueAtIndex(
            componentService.getSelectedIndex() + actionPayload.payload * (componentState.mode === Modes.year ? 10 : 12)
          )
        });
        break;
      case KeyboardActions.cancel:
        componentService.update({ selected: null });
        break;
      case KeyboardActions.focusNext:
        const tabArray = focustTrapList;
        const focusedIndex = tabArray.findIndex(element => element.nativeElement === this.document.activeElement);
        const nextIndex = focusedIndex > -1 && focusedIndex < tabArray.length - 1 ? focusedIndex + 1 : 0;
        tabArray[nextIndex].nativeElement.focus();
        break;
      case KeyboardActions.selectCurrent:
        if (
          focustTrapList.some(
            element =>
              element.nativeElement === this.document.activeElement && element.nativeElement.tagName === 'BUTTON'
          )
        ) {
          return;
        }
        componentService.update({ value: componentState.selected });
    }
    event.preventDefault();
  }
}
